import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { distinctUntilKeyChanged } from "rxjs/operators";
import { AuthService } from "./auth.service";
declare const gtag: Function;

@Injectable({
  providedIn: "root",
})
export class GoogleAnalyticsService {
  private gaUserId: string = null;

  constructor(
    public auth: AuthService,
    private router: Router
  ) {}

  initialize() {
    this.addScriptTag();
    this.watchForUserChanges();
  }

  setUser() {
    gtag("set", { user_id: this.gaUserId });
  }

  getDefaultPropertiesSent() {
    return {
      page_referrer: "", // Override GA
      page_location: this.sanitize(window.location.toString()), // Override GA
    };
  }

  getTextFromEvent(event) {
    const element = event.currentTarget || event.target;
    return element?.innerText || element?.value;
  }

  getHrefFromEvent(event) {
    const element = event.currentTarget || event.target;
    return element?.getAttribute("href");
  }

  addPageView(routerEvent) {
    // Comment if Google Analytics auto captures (i.e., enhanced measurements was turned on within GA
    // or if "page changes based on browser history events" was enabled)
    this.sendEvent("page_view", {
      page_path: this.sanitize(routerEvent.urlAfterRedirects),
    });
  }

  addMenuClick(name: string, parentName: string, path: string) {
    this.sendEvent("menu_click", {
      display_name: name,
      parent_display_name: parentName,
      target_path: path,
    });
  }

  addPageLinkClick(name: string, path: string) {
    this.sendEvent("page_link_click", {
      display_name: name,
      target_path: path,
    });
  }

  addPageLinkClickFromEvent(event) {
    const name = this.getTextFromEvent(event);
    const path = this.getHrefFromEvent(event);
    this.sendEvent("page_link_click", {
      display_name: name,
      target_path: path,
    });
  }

  addButtonClick(name: string, path: string) {
    this.sendEvent("button_click", { display_name: name, target_path: path });
  }

  addClickBasedOnType(event: Event, path: string) {
    const element = event.currentTarget || event.target;
    if (element) {
      const tagName: string = element["tagName"];
      if (tagName?.toUpperCase() === "A") {
        this.addPageLinkClick(this.getTextFromEvent(event), path);
        return;
      }
    }
    this.addButtonClick(this.getTextFromEvent(event), path);
  }

  addPaginationInformation(
    name: string,
    hasPagination: boolean,
    pageSize: number,
    pageNumber: number,
    collectionSize: number
  ) {
    this.sendEvent("pagination", {
      display_name: name,
      has_pagination: hasPagination,
      page_size: pageSize,
      page_number: pageNumber,
      collection_size: collectionSize,
    });
  }

  addPageSizeChange(
    name: string,
    newPageSize: number,
    pageNumber: number,
    collectionSize: number
  ) {
    this.sendEvent("page_size", {
      display_name: name,
      page_size: newPageSize,
      page_number: pageNumber,
      collection_size: collectionSize,
    });
  }

  addPageNumberChange(
    name: string,
    pageNumber: number,
    pageSize: number,
    collectionSize: number
  ) {
    this.sendEvent("page_number", {
      display_name: name,
      page_number: pageNumber,
      page_size: pageSize,
      collection_size: collectionSize,
    });
  }

  addDocumentDownloadClick(type, id = null) {
    // Policy Documents and Billing Documents have a masked id, hence why the id can be null
    this.sendEvent("document_download_click", {
      document_type: type,
      item_id: id,
    });
  }

  addSelect(
    type: string,
    id: string,
    selectByDefault: boolean,
    targetPath: string = null
  ) {
    this.sendEvent("select_content", {
      content_type: type,
      item_id: id,
      select_by_default: selectByDefault,
      current_path: this.router.url.split("?")[0],
      target_path: targetPath,
    });
  }

  addFormSubmit(formName: string, formSubmitText: string) {
    this.sendEvent("form_submit", {
      form_name: formName,
      form_submit_text: formSubmitText,
    }); // Form submit, form name, and form submit text are from GA4 (not custom)
  }

  addLogout(autoTimeout: boolean = false) {
    this.sendEvent("logout", { timeout: autoTimeout });
  }

  addHover(text: string) {
    this.sendEvent("hover", { display_name: text });
  }

  private sendEvent(eventName: string, properties: any) {
    gtag("event", eventName, {
      ...properties,
      ...this.getDefaultPropertiesSent(),
    });
  }

  private watchForUserChanges() {
    this.auth.account$
      .pipe(distinctUntilKeyChanged("username"))
      .subscribe((user) => {
        this.gaUserId = user.username ? user.username : null;
        this.setUser();
        if (this.gaUserId) {
          this.sendEvent("login", {});
        }
      });
  }

  private addScriptTag() {
    let gtagScriptElement: HTMLScriptElement = document.createElement("script");
    gtagScriptElement.async = true;
    gtagScriptElement.src =
      "https://www.googletagmanager.com/gtag/js?id=" +
      environment.googleAnalyticsMeasurementId;
    document.head.prepend(gtagScriptElement);
    gtag("config", environment.googleAnalyticsMeasurementId, {
      send_page_view: false,
    }); // To run in debug mode, add  'debug_mode': true
  }

  private sanitize(stringToSanitize: string) {
    let sanitizedString = stringToSanitize;
    const sensitiveQueryParameters = [
      "code",
      "email",
      "user",
      "username",
      "token",
      "emailaddress",
      "userid",
      "profileid",
      "password",
      "reset",
    ];
    const lastIndexOf = stringToSanitize?.lastIndexOf("?");
    if (lastIndexOf > -1) {
      const queryString: string = stringToSanitize.substring(
        lastIndexOf,
        stringToSanitize.length
      );
      if (sensitiveQueryParameters.some((x) => queryString.indexOf(x) > -1)) {
        sanitizedString =
          stringToSanitize.substring(0, lastIndexOf) + "?redacted";
      }
    }
    return sanitizedString;
  }
}
